import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Trash2, ChevronDown, Plus } from 'lucide-react';
import { Route as rootRoute } from './__root';
import {
  endpointsQueryOptions,
  tenantEndpointQueryOptions,
  useAssignEndpoint,
  useDeleteEndpoint,
  type Endpoint,
} from '../lib/queries/storage-endpoints';
import {
  customersQueryOptions,
  type TenantRow,
} from '../lib/queries/customers';
import EndpointForm from '../components/storage/EndpointForm';
import { requireAdmin } from '../lib/guards';
import Modal from '../components/Modal';
import { Button } from '../components/Button';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s3-endpoints',
  beforeLoad: requireAdmin,
  component: S3EndpointsPage,
});

// Mirrors the customer list so a tenant's plan reads the same colour everywhere.
const PLAN_STYLE: Record<string, string> = {
  Enterprise: 'border-amber-500/50 text-amber-400',
  Growth: 'border-blue-500/50 text-blue-400',
  Starter: 'border-white/20 text-muted',
};

// Same tenant-status colours as the customer list.
const STATUS_DOT: Record<string, string> = {
  active: 'bg-green-400',
  pending: 'bg-yellow-400',
  suspended: 'bg-red-400',
};

const selectCls =
  'w-full rounded border border-white/10 bg-white/4 px-3 py-2 text-sm text-primary/50 focus:border-[--color-accent] focus:outline-none';

/**
 * Modal.Window clones its child with `onCloseModal`, so this owns the mutation:
 * the failure reason (assigned tenants, existing files) is most useful right
 * where the user is deciding, and the dialog stays open so they can read it.
 */
function DeleteEndpointConfirm({
  endpoint,
  onCloseModal,
}: {
  endpoint: Endpoint;
  onCloseModal?: () => void;
}) {
  const del = useDeleteEndpoint();

  return (
    <div className="p-6">
      <p className="text-primary font-medium">Delete endpoint</p>
      <p className="text-muted mt-2 text-sm">
        <span className="text-primary font-medium">{endpoint.name}</span> and
        its stored credential will be permanently removed. This cannot be
        undone.
      </p>

      {del.isError && (
        <p className="text-danger mt-4 rounded border border-red-500/30 bg-red-500/5 p-3 text-xs">
          {(del.error as Error).message}
        </p>
      )}

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button
          variant="secondary"
          onClick={onCloseModal}
          disabled={del.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={del.isPending}
          onClick={() => del.mutate(endpoint.id, { onSuccess: onCloseModal })}
        >
          <Trash2 size={14} />
          {del.isPending ? 'Deleting…' : 'Delete endpoint'}
        </Button>
      </div>
    </div>
  );
}

function EndpointCard({ endpoint }: { endpoint: Endpoint }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-bg-elevated border-primary/8 overflow-hidden rounded-lg border">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <div className="min-w-0">
          <p className="text-primary font-medium">{endpoint.name}</p>
          <p className="text-subtle mt-0.5 truncate font-mono text-xs">
            {endpoint.endpointUrl}
          </p>
        </div>
        <div className="ml-4 flex shrink-0 items-center gap-3">
          <span className="text-muted text-xs">
            {endpoint.tenantCount}{' '}
            {endpoint.tenantCount === 1 ? 'tenant' : 'tenants'}
          </span>
          <ChevronDown
            size={16}
            className={[
              'text-muted transition-transform duration-200',
              open ? 'rotate-180' : '',
            ].join(' ')}
          />
        </div>
      </button>

      {open && (
        <div className="border-primary/8 border-t p-5">
          {/* Editable in place. `key` resets the form if the row changes
              underneath (e.g. after a refetch). */}
          <EndpointForm key={endpoint.id} endpoint={endpoint} inline />

          <div className="border-primary/8 mt-5 flex items-center justify-between border-t pt-4">
            <p className="text-subtle text-[0.625rem]">
              {endpoint.tenantCount > 0
                ? `Assigned to ${endpoint.tenantCount} tenant${endpoint.tenantCount === 1 ? '' : 's'} — unassign them before deleting.`
                : 'Not assigned to any tenant.'}
            </p>
            <Modal>
              <Modal.Open opens="delete-endpoint">
                <button className="text-danger border-danger hover:text-primary flex items-center gap-1.5 rounded border px-3 py-1 text-xs transition-colors disabled:opacity-40">
                  <Trash2 size={12} />
                  Delete
                </button>
              </Modal.Open>

              <Modal.Window name="delete-endpoint">
                <DeleteEndpointConfirm endpoint={endpoint} />
              </Modal.Window>
            </Modal>
          </div>
        </div>
      )}
    </div>
  );
}

// Shared per-tenant assignment state. React Query dedupes by key, so the
// desktop row and the mobile card mounting this in parallel share one request.
function useTenantAssignment(tenantId: string) {
  const { data } = useQuery(tenantEndpointQueryOptions(tenantId));
  const assign = useAssignEndpoint(tenantId);
  return { assignedId: data?.endpointId ?? '', assign };
}

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span
      className={[
        'w-fit rounded border px-2 py-0.5 text-xs font-medium',
        PLAN_STYLE[plan] ?? 'text-muted border-white/20',
      ].join(' ')}
    >
      {plan}
    </span>
  );
}

function EndpointSelect({
  assignedId,
  assign,
  endpoints,
}: {
  assignedId: string;
  assign: ReturnType<typeof useAssignEndpoint>;
  endpoints: Endpoint[];
}) {
  return (
    <>
      {/* Blank option unassigns (endpointId → null). */}
      <select
        value={assignedId}
        disabled={assign.isPending}
        onChange={(e) => assign.mutate(e.target.value || null)}
        className={selectCls}
      >
        <option value="" className="bg-bg-elevated text-primary">
          Endpoint name
        </option>
        {endpoints.map((ep) => (
          <option
            key={ep.id}
            value={ep.id}
            className="bg-bg-elevated text-primary"
          >
            {ep.name}
          </option>
        ))}
      </select>
      {assign.isError && (
        <p className="text-danger mt-1 text-xs">
          {(assign.error as Error).message}
        </p>
      )}
    </>
  );
}

// Read-only: the object key is a fixed convention, not a stored field
// (see storage.service.ts — `${tenantId}/${conversationId}/${fileId}`).
function storagePath(tenantId: string, assignedId: string) {
  return assignedId ? `${tenantId}/{conversationId}/{fileId}` : '—';
}

// Desktop layout: one table row.
function TenantAssignmentRow({
  tenant,
  endpoints,
}: {
  tenant: TenantRow;
  endpoints: Endpoint[];
}) {
  const { assignedId, assign } = useTenantAssignment(tenant.id);

  return (
    <tr className="border-primary/6 border-b last:border-0">
      <td className="px-4 py-3">
        <p className="text-primary truncate font-medium">{tenant.name}</p>
        <p className="text-subtle mt-0.5 truncate font-mono text-[0.625rem] tracking-widest uppercase">
          {tenant.id}
        </p>
      </td>
      <td className="px-4 py-3">
        <PlanBadge plan={tenant.plan} />
      </td>
      <td className="px-4 py-3">
        <EndpointSelect
          assignedId={assignedId}
          assign={assign}
          endpoints={endpoints}
        />
      </td>
      <td className="px-4 py-3">
        <p className="text-subtle truncate font-mono text-xs">
          {storagePath(tenant.id, assignedId)}
        </p>
      </td>
    </tr>
  );
}

// Mobile layout: compact card — status dot, inline id, path on the right,
// and a narrower endpoint select below.
function TenantAssignmentCard({
  tenant,
  endpoints,
}: {
  tenant: TenantRow;
  endpoints: Endpoint[];
}) {
  const { assignedId, assign } = useTenantAssignment(tenant.id);

  return (
    <li className="border-primary/6 space-y-3 border-b p-4 last:border-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center justify-between gap-2 md:justify-start">
          <span
            className={[
              'h-2 w-2 shrink-0 rounded-full',
              STATUS_DOT[tenant.status] ?? 'bg-white/40',
            ].join(' ')}
          />
          <p className="text-subtle truncate font-medium">{tenant.name}</p>
          {/* <p className="text-subtle shrink-0 font-mono text-xs tracking-wide uppercase">
            {tenant.id}
          </p> */}
        </div>
        <p className="text-subtle max-w-[40%] shrink-0 truncate font-mono text-xs">
          {storagePath(tenant.id, assignedId)}
        </p>
      </div>

      <div className="max-w-[18rem]">
        <EndpointSelect
          assignedId={assignedId}
          assign={assign}
          endpoints={endpoints}
        />
      </div>
    </li>
  );
}

function TenantAssignments({ endpoints }: { endpoints: Endpoint[] }) {
  const { data, isLoading } = useQuery(customersQueryOptions({ limit: 100 }));
  const tenants = data?.tenants ?? [];
  const status = isLoading
    ? 'Loading tenants…'
    : tenants.length === 0
      ? 'No tenants yet.'
      : null;

  return (
    <div className="space-y-3">
      <p className="text-subtle text-xs font-semibold tracking-widest uppercase">
        Tenant assignments
      </p>

      <div className="bg-bg-elevated border-primary/8 overflow-hidden rounded-lg border">
        {status ? (
          <p className="text-muted px-4 py-10 text-center text-sm">{status}</p>
        ) : (
          <>
            {/* Desktop: table */}
            <table className="hidden w-full table-fixed text-sm md:table">
              <thead>
                <tr className="border-primary/8 border-b">
                  <th className="text-subtle w-[30%] px-4 py-2.5 text-left text-xs uppercase">
                    Tenant
                  </th>
                  <th className="text-subtle w-[14%] px-4 py-2.5 text-left text-xs uppercase">
                    Plan
                  </th>
                  <th className="text-subtle w-[28%] px-4 py-2.5 text-left text-xs uppercase">
                    S3 endpoint
                  </th>
                  <th className="text-subtle w-[28%] px-4 py-2.5 text-left text-xs uppercase">
                    Storage path pattern
                  </th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <TenantAssignmentRow
                    key={t.id}
                    tenant={t}
                    endpoints={endpoints}
                  />
                ))}
              </tbody>
            </table>

            {/* Mobile: stacked cards */}
            <ul className="md:hidden">
              {tenants.map((t) => (
                <TenantAssignmentCard
                  key={t.id}
                  tenant={t}
                  endpoints={endpoints}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function S3EndpointsPage() {
  const { data, isLoading } = useQuery(endpointsQueryOptions());

  return (
    <div className="space-y-6 p-6">
      <p className="text-muted text-sm">
        S3 endpoints available for chat file uploads. Assign one to each tenant
        below.
      </p>

      <div className="space-y-3">
        <p className="text-subtle text-xs font-semibold tracking-widest uppercase">
          Configured endpoints
        </p>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-lg bg-white/6"
              />
            ))}
          </div>
        ) : data?.endpoints.length ? (
          data.endpoints.map((e) => <EndpointCard key={e.id} endpoint={e} />)
        ) : (
          <div className="border-primary/8 rounded-lg border border-dashed p-10 text-center">
            <p className="text-muted text-sm">
              No storage endpoints configured yet.
            </p>
          </div>
        )}
      </div>

      <TenantAssignments endpoints={data?.endpoints ?? []} />

      {/* Mobile-only FAB → opens the add-endpoint modal. Hidden on md+ where
          the header already exposes "Add Endpoint". */}
      <Modal>
        <Modal.Open opens="add-endpoint">
          <button
            type="button"
            aria-label="Add S3 endpoint"
            className="bg-accent fixed right-4 bottom-20 z-40 flex h-10 w-10 items-center justify-center rounded-full text-white shadow-lg transition-transform active:scale-95 md:hidden"
          >
            <Plus size={24} />
          </button>
        </Modal.Open>

        <Modal.Window name="add-endpoint">
          <EndpointForm />
        </Modal.Window>
      </Modal>
    </div>
  );
}
